import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { PiPaperclipLight, PiPaperPlaneLight, PiXLight } from 'react-icons/pi';
import { Tooltip } from '@mui/material';
import Spinner from '../shared/TailwindComponents/Spinner';

interface NewInputBarProps {
  onSendMessage: (message: string, attachments: any) => void;
  uploadingImages: boolean;
  chatId: string;
  selectedChat: any;
  replyToId: string | null;
  clearReply: () => void;
  messages: any[];
  userMap: Record<string, any>;
  onTyping: () => void;
}

const NewInputBar: React.FC<NewInputBarProps> = ({
  onSendMessage,
  uploadingImages,
  chatId,
  selectedChat,
  replyToId,
  clearReply,
  messages,
  userMap,
  onTyping,
}) => {
  const { t } = useTranslation();
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const repliedMsg = replyToId ? messages.find(m => m.id === replyToId || m.MessageID === replyToId) : null;

  const handleSendMessage = () => {
    if (newMessage.trim() || attachments.length) {
      onSendMessage(newMessage, attachments.length ? attachments : []);
      setNewMessage('');
      setAttachments([]);
      clearReply();
    }
  };

  // const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
  //   setNewMessage(e.target.value);
  //   onTyping();
  // };
  const lastTypingRef = useRef<number>(0);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    const now = Date.now();
    if (now - lastTypingRef.current > 600) {
      lastTypingRef.current = now;
      onTyping();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const onFilesChange = (newFiles: File[]) => {
    const totalSelectedCount = attachments.length;
    const incomingCount = newFiles.length;
    if (totalSelectedCount + incomingCount > 5) {
      alert('You can only upload up to 5 files in total.');
      return;
    }
    setAttachments(prev => [...prev, ...newFiles]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onFilesChange(Array.from(e.target.files));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files) {
      onFilesChange(Array.from(e.dataTransfer.files));
    }
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      {/* Reply preview */}
      {repliedMsg && (
        <div className="flex items-center gap-2 mb-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border-l-4 border-primary-500">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
              Replying to {userMap[repliedMsg.sender || repliedMsg.SenderID]?.Name || t('Unknown')}
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
              {typeof repliedMsg.content === 'string' ? repliedMsg.content : ''}
            </p>
          </div>
          <button
            onClick={clearReply}
            className="p-1 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded transition-colors flex-shrink-0"
          >
            <PiXLight className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      )}

      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {attachments.map((file, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg"
            >
              <span className="text-xs text-gray-700 dark:text-gray-300 truncate max-w-xs">
                {file.name}
              </span>
              <button
                onClick={() => handleRemoveAttachment(index)}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <PiXLight className="text-sm text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input area */}
      <div
        className="flex gap-2 items-end"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* File upload button */}
        <Tooltip title={t('Attach files')}>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImages}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
          >
            {uploadingImages ? (
              <Spinner size="glyph" />
            ) : (
              <PiPaperclipLight className="text-xl" />
            )}
          </button>
        </Tooltip>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
          disabled={uploadingImages}
        />

        {/* Message input */}
        <div className="flex-1 relative">
          <textarea
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t("Type a message... (Shift+Enter for new line)")}
            className="glass w-full px-4 py-2 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none min-h-10 max-h-24"
            rows={1}
            style={{ height: 'auto', maxHeight: '100px' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 100) + 'px';
            }}
          />
        </div>

        {/* Send button */}
        <Tooltip title={t("Send (Enter)")}>
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() && !attachments.length}
            className="p-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <PiPaperPlaneLight className="text-xl" />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default NewInputBar;
